import { createContext, useCallback, useContext, useRef } from "react";
import { Box } from "@mui/material";
import { register, unregister } from "./revealEngine";

// Scroll reveals: content fades and rises into place once, as it enters view.
//
//   <Reveal>...</Reveal>                     a wrapper that reveals as a unit
//   const r = useReveal();                   attach to an element you already have:
//   <Card ref={r.ref} {...r.props} />        no wrapper, so grid spans keep working
//
// Options:
//   trigger  "view" (default) when scrolled into view; "mount" plays straight away,
//            for above-the-fold entrances such as a page hero
//   index    stagger position for "mount" (view reveals stagger on their own,
//            in reading order, when several arrive together)
//   variant  "fade" to fade without rising (full-bleed artwork)
//
// Rule of thumb: reveal the LEAVES - a heading, a paragraph, a card, a figure -
// not the containers that hold them. And never nest: a reveal inside a reveal
// is switched off automatically (RevealScope), so the inner content arrives
// with its parent instead of fading twice.
//
// The engine that decides timing, and why it adapts to scroll speed, is in
// revealEngine.js; the keyframes are in Theme.js (MuiCssBaseline).

const RevealScopeContext = createContext(false);

// Marks everything inside as already covered by an ancestor's reveal. It can
// only switch reveals off: an inactive scope inside an active one stays active.
export const RevealScope = ({ children, active = true }) => {
  const insideReveal = useContext(RevealScopeContext);
  return (
    <RevealScopeContext.Provider value={insideReveal || Boolean(active)}>
      {children}
    </RevealScopeContext.Provider>
  );
};

const noopRef = () => {};

export const useReveal = ({ trigger = "view", index = 0, variant, disabled = false } = {}) => {
  const insideReveal = useContext(RevealScopeContext);
  const off = disabled || insideReveal;
  const nodeRef = useRef(null);

  const ref = useCallback(
    (node) => {
      if (nodeRef.current && nodeRef.current !== node) unregister(nodeRef.current);
      nodeRef.current = node;
      if (node && !node.hasAttribute("data-revealed")) register(node, { trigger, index });
    },
    [trigger, index]
  );

  if (off) return { ref: noopRef, props: {}, active: false };
  return {
    ref,
    props: { "data-reveal": variant || "" },
    active: true,
  };
};

export const Reveal = ({ trigger, index, variant, disabled, children, ...boxProps }) => {
  const reveal = useReveal({ trigger, index, variant, disabled });
  return (
    <Box ref={reveal.ref} {...reveal.props} {...boxProps}>
      <RevealScope active={reveal.active}>{children}</RevealScope>
    </Box>
  );
};
